import {
  Card,
  DatePicker,
  Select,
  Space,
  Table,
  Tag,
  Typography,
} from 'antd';
import { ProfileOutlined } from '@ant-design/icons';
import type { TableProps } from 'antd';
import { EmptyState } from '@/shared/components/ui/empty-state';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import dayjs, { type Dayjs } from 'dayjs';
import {
  financeApi,
  type LedgerEntry,
  type SourceType,
} from '@/api/finance';
import { PageHeader } from '@/shared/components/page-header';
import { ExportButton } from '@/shared/components/ui';

const PKR = (n: number | string) =>
  Number(n).toLocaleString('en-PK', { maximumFractionDigits: 2 });

const SOURCE_LABEL: Record<SourceType, string> = {
  FEE_RECEIPT: 'Fee receipt',
  EXPENSE: 'Expense',
  SALARY: 'Salary',
  STORE_SALE: 'Store sale',
  STORE_PURCHASE: 'Store purchase',
  VENDOR_PAYMENT: 'Vendor payment',
  OTHER_INCOME: 'Other income',
  JOURNAL: 'Journal',
};

export function FinanceDaybookPage() {
  const [range, setRange] = useState<[Dayjs, Dayjs]>([
    dayjs().startOf('month'),
    dayjs().endOf('month'),
  ]);
  const [accountId, setAccountId] = useState<number | undefined>();

  const params = {
    from: range[0].format('YYYY-MM-DD'),
    to: range[1].format('YYYY-MM-DD'),
    accountId,
  };
  const { data, isFetching } = useQuery({
    queryKey: ['finance', 'daybook', params],
    queryFn: () => financeApi.daybook(params),
  });

  const { data: accounts } = useQuery({
    queryKey: ['finance', 'accounts'],
    queryFn: financeApi.listAccounts,
    staleTime: 60_000,
  });

  const accountOptions = (accounts ?? [])
    .filter((a) => a.postingAccount)
    .map((a) => ({ value: a.id, label: `${a.code} · ${a.name}` }));

  const totalDr = (data ?? [])
    .filter((e) => e.entryType === 'DEBIT')
    .reduce((acc, e) => acc + Number(e.amount), 0);
  const totalCr = (data ?? [])
    .filter((e) => e.entryType === 'CREDIT')
    .reduce((acc, e) => acc + Number(e.amount), 0);

  const columns: TableProps<LedgerEntry>['columns'] = [
    {
      title: 'Date',
      dataIndex: 'entryDate',
      width: 130,
      render: (d: string) => dayjs(d).format('D MMM YYYY'),
    },
    {
      title: 'Account',
      render: (_, row) => (
        <Space orientation="vertical" size={0}>
          <Typography.Text strong>{row.accountName ?? '—'}</Typography.Text>
          {row.accountCode && (
            <Typography.Text type="secondary" style={{ fontSize: 11 }}>{row.accountCode}</Typography.Text>
          )}
        </Space>
      ),
    },
    {
      title: 'Source',
      width: 160,
      render: (_, row) => (
        <Space orientation="vertical" size={0}>
          <Tag>{SOURCE_LABEL[row.sourceType]}</Tag>
          <Typography.Text type="secondary" style={{ fontSize: 11 }}>#{row.sourceId}</Typography.Text>
        </Space>
      ),
    },
    {
      title: 'Description',
      dataIndex: 'description',
      render: (d: string | null, row) => (
        <Space orientation="vertical" size={0}>
          <Typography.Text>{d ?? '—'}</Typography.Text>
          {row.reversal && <Tag color="default">Reversal</Tag>}
        </Space>
      ),
    },
    {
      title: 'DR',
      width: 130,
      align: 'right',
      render: (_, row) =>
        row.entryType === 'DEBIT' ? (
          <Typography.Text strong>PKR {PKR(row.amount)}</Typography.Text>
        ) : (
          <Typography.Text type="secondary">—</Typography.Text>
        ),
    },
    {
      title: 'CR',
      width: 130,
      align: 'right',
      render: (_, row) =>
        row.entryType === 'CREDIT' ? (
          <Typography.Text strong>PKR {PKR(row.amount)}</Typography.Text>
        ) : (
          <Typography.Text type="secondary">—</Typography.Text>
        ),
    },
  ];

  return (
    <div style={{ display: 'grid', gap: 20 }}>
      <PageHeader
        eyebrow="Module / Finance"
        title="Daybook"
        description="Every ledger entry posted in the chosen window. Filter to a single account if you want a per-account ledger."
        count={data ? { value: data.length, label: 'entries' } : undefined}
        actions={
          <ExportButton
            items={[{
              kind: 'xlsx',
              url: '/reports/exports/daybook.xlsx',
              params,
              fileName: `daybook-${params.from}-to-${params.to}.xlsx`,
              disabled: !data,
            }]}
          />
        }
      />

      <Space wrap size={12}>
        <DatePicker.RangePicker
          value={range}
          onChange={(r) => r && r[0] && r[1] && setRange([r[0], r[1]])}
          format="DD MMM YYYY"
          allowClear={false}
        />
        <Select
          placeholder="Any account"
          allowClear
          value={accountId}
          onChange={setAccountId}
          options={accountOptions}
          showSearch
          optionFilterProp="label"
          style={{ minWidth: 280 }}
        />
        {data && data.length > 0 && (
          <Space size={6}>
            <Tag color="processing">DR PKR {PKR(totalDr)}</Tag>
            <Tag color="warning">CR PKR {PKR(totalCr)}</Tag>
          </Space>
        )}
      </Space>

      {data && data.length === 0 && !isFetching ? (
        <Card>
          <EmptyState
            icon={<ProfileOutlined />}
            title="No ledger entries"
            description="Nothing was posted to the ledger in this date range. Try widening the dates or clearing the account filter."
          />
        </Card>
      ) : (
        <Card styles={{ body: { padding: 0 } }}>
          <Table
            rowKey="id"
            size="middle"
            loading={isFetching}
            columns={columns}
            dataSource={data ?? []}
            pagination={{ pageSize: 50, showSizeChanger: true }}
          />
        </Card>
      )}
    </div>
  );
}
